import type { NotificationReaderContext } from '@/modules/notifications/reads.api'
import { isNotificationReadUnavailable } from '@/modules/notifications/reads.api'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { useNotificationReadSession } from './useNotificationReadSession'
import {
  executeNotificationReader,
  useNotificationReaderRefused,
} from './notification-reader-cache'

/** No previous-owner placeholder; an outage cannot reverse a known refusal. */
export function useNotificationScopedQuery<T>(
  prefix: readonly unknown[],
  session: ReturnType<typeof useNotificationReadSession>,
  read: (context: NotificationReaderContext) => Promise<T>,
  options: {
    enabled?: boolean
    staleTime?: number
    refetchInterval?: number
  } = {},
) {
  const enabled = options.enabled !== false && session.enabled
  const key = [...prefix, session.key]
  const client = useQueryClient()
  const query = useQuery<T>({
    queryKey: key,
    queryFn: ({ signal }) =>
      executeNotificationReader(client, key, session, signal, read),
    enabled,
    staleTime: options.staleTime,
    refetchInterval: options.refetchInterval,
    gcTime: 60000,
    retry: (failures, error) =>
      !isNotificationReadUnavailable(error) && failures < 2,
  })
  const refused = useNotificationReaderRefused(client, key)
  return {
    ...query,
    data:
      enabled && !refused && !isNotificationReadUnavailable(query.error)
        ? query.data
        : undefined,
  }
}
